/**
 * Svelte action: fade an element in once it scrolls into view.
 *
 * Adds `.is-visible` to the host once the IntersectionObserver fires.
 * Pair with the `.reveal` class in app.css (which sets the start state).
 * Respects `prefers-reduced-motion`.
 */
import type { Action } from 'svelte/action';

interface Options {
	threshold?: number;
	once?: boolean;
}

export const scrollReveal: Action<HTMLElement, Options | undefined> = (node, options) => {
	const opts: Required<Options> = {
		threshold: options?.threshold ?? 0.12,
		once: options?.once ?? true
	};

	// Reduced-motion users get the final state immediately.
	const reduced =
		typeof window !== 'undefined' &&
		window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

	if (reduced || typeof IntersectionObserver === 'undefined') {
		node.classList.add('is-visible');
		return {};
	}

	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					node.classList.add('is-visible');
					if (opts.once) observer.unobserve(node);
				} else if (!opts.once) {
					node.classList.remove('is-visible');
				}
			}
		},
		{ threshold: opts.threshold }
	);

	observer.observe(node);

	return {
		destroy() {
			observer.disconnect();
		}
	};
};
